import { API_URL } from "../../../api";
import { setCurrentElevenLabsAudio } from "./textToSpeechService";

const TOKEN_URL = `${API_URL}/elevenlabs/token`;
const SAMPLE_RATE = 24000;
const MAX_CACHE_ENTRIES = 8;

const audioCache = new Map();

let playbackId = 0;
let playbackSocket = null;

const pcmPlayer = {
  context: null,
  nextTime: 0,
  sources: [],
};

const cacheKey = (voiceId, text) => `${voiceId}::${text}`;

const postWithCredentials = (url, body, contentType = "application/json") =>
  fetch(url, {
    method: "POST",
    body,
    credentials: "include",
    headers: {
      "Content-Type": contentType,
    },
  });

const fetchToken = async () => {
  const tokenRes = await fetch(TOKEN_URL,{
    method: "POST",
    credentials: "include",
  });
  const tokenJson = await tokenRes.json();
  return tokenJson.data.token;
};

const takeToken = () => fetchToken();


const ensureAudioContext = async () => {

  if (!pcmPlayer.context) {

    pcmPlayer.context =
      new AudioContext({
        sampleRate: SAMPLE_RATE
      });
  }

  return pcmPlayer.context;
};

let audioUnlocked = false;

export const unlockAudioContext = async () => {

  if (audioUnlocked) {
    return;
  }

  if (!pcmPlayer.context) {

    pcmPlayer.context =
      new AudioContext({
        sampleRate: SAMPLE_RATE
      });

  }

  if (
    pcmPlayer.context.state ===
    'suspended'
  ) {

    await pcmPlayer.context.resume();

  }

  audioUnlocked = true;
};

const stopPcmPlayer = () => {
  pcmPlayer.sources.forEach((source) => {
    try {
      source.stop();
    } catch (error) {
      // already stopped
    }
  });
  pcmPlayer.sources = [];
  pcmPlayer.nextTime = 0;
};

export const stopElevenLabsPlayback = () => {
  playbackId += 1;
  if (playbackSocket) {
    try {
      playbackSocket.close();
    } catch (error) {
      // already closing
    }
    playbackSocket = null;
  }
  stopPcmPlayer();
};

const concatInt16 = (chunks) => {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Int16Array(length);
  let offset = 0;
  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });
  return merged;
};

const base64ToInt16 = (base64Audio) => {
  const binary = atob(base64Audio);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const sampleCount = Math.floor(bytes.byteLength / 2);
  const samples = new Int16Array(sampleCount);
  const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2);
  for (let i = 0; i < sampleCount; i++) {
    samples[i] = view.getInt16(i * 2, true);
  }
  return samples;
};

const enqueuePcm = (samples) => {

  const context = pcmPlayer.context;
  const float32 = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    float32[i] = samples[i] / 32768;
  }
  const buffer = context.createBuffer(1, float32.length, SAMPLE_RATE);
  buffer.copyToChannel(float32, 0);
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);
  const startAt = Math.max(pcmPlayer.nextTime, context.currentTime);
  source.start(startAt);
  pcmPlayer.nextTime = startAt + buffer.duration;
  pcmPlayer.sources.push(source);
};

const rememberCache = (key, samples) => {
  audioCache.set(key, samples);
  if (audioCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = audioCache.keys().next().value;
    audioCache.delete(oldestKey);
  }
};

const openTtsSocket = (voiceId, token) =>
  new WebSocket(
    `wss://api.elevenlabs.io/v1/text-to-speech/${voiceId}/stream-input?model_id=eleven_turbo_v2_5&output_format=pcm_24000&auto_mode=true&single_use_token=${encodeURIComponent(token)}`
  );

const sendTtsText = (ws, text) => {
  ws.send(
    JSON.stringify({
      text: " ",
      voice_settings: {
        stability: 0.4,
        similarity_boost: 0.75,
        style: 0.35,
        use_speaker_boost: true,
      },
    })
  );
  ws.send(JSON.stringify({ text, flush: true }));
  ws.send(JSON.stringify({ text: "" }));
};

const playCachedSamples = async (samples) => {
  await ensureAudioContext();
  stopPcmPlayer();
  enqueuePcm(samples);
  setCurrentElevenLabsAudio({
    pause: stopPcmPlayer,
    currentTime: 0,
  });
};

export const hasElevenLabsCache = (text, voiceId) =>
  Boolean(text?.trim() && voiceId && audioCache.has(cacheKey(voiceId, text)));

export const elevenLabsEngine = async (text, voiceId) => {



  const key = cacheKey(voiceId, text);
  const currentPlaybackId = playbackId;

  try {
    await ensureAudioContext();

    if (audioCache.has(key)) {
      if (currentPlaybackId !== playbackId) {
        return;
      }
      await playCachedSamples(audioCache.get(key));
      return;
    }

    const token = await takeToken();
    if (currentPlaybackId !== playbackId) {
      return;
    }

    stopPcmPlayer();
    setCurrentElevenLabsAudio({
      pause: stopPcmPlayer,
      currentTime: 0,
    });

    const chunks = [];
    await new Promise((resolve, reject) => {
      const ws = openTtsSocket(voiceId, token);
      playbackSocket = ws;
      let settled = false;

      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        if (playbackSocket === ws) {
          playbackSocket = null;
        }
        resolve();
      };

      ws.onopen = () => {
        if (currentPlaybackId !== playbackId) {
          ws.close();
          return;
        }
        sendTtsText(ws, text);
      };

      ws.onmessage = (event) => {
        if (currentPlaybackId !== playbackId) {
          ws.close();
          finish();
          return;
        }

        const data = JSON.parse(event.data);
        if (data.audio) {
          const samples = base64ToInt16(data.audio);
          chunks.push(samples);
          enqueuePcm(samples);
        }
        if (data.isFinal) {
          ws.close();
          finish();
        }
      };

      ws.onerror = () => {
        if (!settled) {
          reject(new Error("ElevenLabs websocket failed"));
        }
      };

      ws.onclose = finish;
    });

    if (chunks.length && currentPlaybackId === playbackId) {
      rememberCache(key, concatInt16(chunks));
    }
  } catch (error) {
    if (error?.name === "AbortError") {
      return;
    }
    console.error("ElevenLabs TTS Error", error);
  }
};
